/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ConfigRevision, Node, NodeFilters } from '@/client';
import { ConfigWizardOverlay } from '@/components/ConfigWizardOverlay/ConfigWizardOverlay';
import { ContentContainer } from '@/components/ContentContainer';
import { NodesPageTab } from '@/components/NodesPageTabs/types';
import { NoNodesEmptyState } from '@/components/NoNodesEmptyState';
import { QueryWrapper } from '@/components/QueryWrapper';
import { useMessage } from '@/hooks/message';
import { useAssignConfigRevisionMutation } from '@/hooks/mutation/useAssignConfigRevisionMutation';
import { usePaginatedNodeGroupsQuery } from '@/hooks/query/usePaginatedNodeGroupsQuery';
import { usePaginatedNodesQuery } from '@/hooks/query/usePaginatedNodesQuery';
import { usePaginatedTagsQuery } from '@/hooks/query/usePaginatedTagsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { NodesHeader } from '@/pages/Nodes/NodesHeader';
import { NodesList } from '@/pages/Nodes/NodesList';
import { Container } from '@/primitives/Container';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { UpdateParams } from '@/utils/params';

export function NodesPage() {
  const [message] = useMessage();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useMimicTranslation('node');
  const [selectedNode, setSelectedNode] = useState<Node | undefined>();
  const selectedTenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const configuredNodesQuery = usePaginatedNodesQuery({
    type: 'queryParams',
    params: { filters: { isOperating: true } },
    urlPrefix: 'configured',
  });

  const pendingNodesQuery = usePaginatedNodesQuery({
    type: 'queryParams',
    params: { filters: { isOperating: false }, sort: ['-dateCreated'] },
    urlPrefix: 'pending',
  });

  const archivedNodesQuery = usePaginatedNodesQuery({
    type: 'queryParams',
    params: { filters: { isArchived: true }, sort: ['-dateCreated'] },
    urlPrefix: 'archived',
  });

  const fleetsQuery = usePaginatedNodeGroupsQuery({ type: 'inMemory' });

  const tagsQuery = usePaginatedTagsQuery({});

  const counts = {
    configured: configuredNodesQuery.data?.meta.page.totalItems ?? 0,
    pending: pendingNodesQuery.data?.meta.page.totalItems ?? 0,
    archived: archivedNodesQuery.data?.meta.page.totalItems ?? 0,
    fleets: fleetsQuery.data?.meta.page.totalItems ?? 0,
  };

  const loading = {
    configured: configuredNodesQuery.isLoading,
    pending: pendingNodesQuery.isLoading,
    archived: archivedNodesQuery.isLoading,
    fleets: fleetsQuery.isLoading,
  };

  const getActiveTab = (): NodesPageTab => {
    const filterParam = searchParams.get('nodeFilter');
    if (filterParam === NodesPageTab.ARCHIVED) return NodesPageTab.ARCHIVED;
    if (filterParam === NodesPageTab.PENDING || searchParams.get('showPending') === 'true') return NodesPageTab.PENDING;
    return NodesPageTab.CONFIGURED;
  };
  const activeTab = getActiveTab();

  const queryMap = {
    [NodesPageTab.CONFIGURED]: configuredNodesQuery,
    [NodesPageTab.PENDING]: pendingNodesQuery,
    [NodesPageTab.ARCHIVED]: archivedNodesQuery,
    [NodesPageTab.FLEETS]: configuredNodesQuery,
  };
  const nodesQuery = queryMap[activeTab];

  const updateQueryParams: UpdateParams<Node, NodeFilters> = (params) => {
    const updateCallback = nodesQuery.updateQueryParams;

    const filters: Partial<NodeFilters> = { ...params.filters };
    if (activeTab === NodesPageTab.ARCHIVED) {
      filters.isArchived = true;
    } else {
      filters.isOperating = activeTab === NodesPageTab.CONFIGURED;
    }

    updateCallback({
      ...params,
      filters,
    });
  };

  const handleTabChange = (tab: NodesPageTab) => {
    if (tab === NodesPageTab.ARCHIVED) {
      archivedNodesQuery.updateQueryParams({ filters: { isArchived: true } });
    } else if (tab === NodesPageTab.CONFIGURED || tab === NodesPageTab.PENDING) {
      const isOperating = tab === NodesPageTab.CONFIGURED;
      const updateCallback = isOperating ? configuredNodesQuery.updateQueryParams : pendingNodesQuery.updateQueryParams;
      updateCallback({ filters: { isOperating } });
    }

    setSearchParams((prev) => {
      prev.delete('showPending');
      prev.set('nodeFilter', tab);
      return prev;
    });
  };

  const onAssignConfiguration = (node: Node) => {
    setSelectedNode(node);
  };

  const { mutate: assignConfigRevision } = useAssignConfigRevisionMutation({
    onError: () => {
      message.error(t('feedback.assignNodeConfigError'));
    },
  });

  const onAssign = (nodeToAssign: Node, configRevision: ConfigRevision) => {
    assignConfigRevision({
      nodeIDs: [nodeToAssign.id],
      configID: configRevision.nodeConfigId!,
      revisionNumber: configRevision.revisionNumber,
    });

    setSelectedNode(undefined);
  };

  const onCancel = () => {
    setSelectedNode(undefined);
  };

  const getNodeConfigurationPath = (nodeConfigId: string) =>
    `/tenants/${selectedTenantID}/node-configs/${nodeConfigId}`;

  const hasNoNodes =
    !configuredNodesQuery.isLoading &&
    !configuredNodesQuery.isFetching &&
    !pendingNodesQuery.isLoading &&
    !pendingNodesQuery.isFetching &&
    !archivedNodesQuery.isLoading &&
    !archivedNodesQuery.isFetching &&
    (configuredNodesQuery.data?.meta.page.totalItems ?? 0) === 0 &&
    (pendingNodesQuery.data?.meta.page.totalItems ?? 0) === 0 &&
    (archivedNodesQuery.data?.meta.page.totalItems ?? 0) === 0;

  return (
    <>
      <ContentContainer width="100%">
        <Container>
          <NodesHeader activeTab={activeTab} counts={counts} loading={loading} onTabChange={handleTabChange} />
          {hasNoNodes ? (
            <NoNodesEmptyState />
          ) : (
            <QueryWrapper queryResult={tagsQuery}>
              {(tagsData) => (
                <QueryWrapper queryResult={nodesQuery}>
                  {(nodesData) => (
                    <NodesList
                      tenantID={selectedTenantID!}
                      tags={tagsData.data}
                      onParamsChange={updateQueryParams}
                      nodesState={activeTab}
                      nodesData={nodesData}
                      onAssignConfiguration={onAssignConfiguration}
                      getNodeConfigurationPath={getNodeConfigurationPath}
                    />
                  )}
                </QueryWrapper>
              )}
            </QueryWrapper>
          )}
        </Container>
      </ContentContainer>
      <ConfigWizardOverlay node={selectedNode} onCancel={onCancel} onAssign={onAssign} />
    </>
  );
}
